import { createInertiaApp, type ResolvedComponent } from "@inertiajs/react";

import { ThemeProvider } from "@/components/ThemeProvider";
import { readCspNonce } from "@/lib/csp";
import "@/lib/consent.css";
import { initMonitoring } from "@/lib/monitoring";
import { createPageResolver } from "@/lib/page-resolver";
import { initRouterErrorReporting } from "@/lib/router-error-reporting";
import { registerServiceWorker } from "@/lib/service-worker";
import {
  clearStartupReloadMark,
  recoverFromStartupFailure,
  safeSessionStorage,
} from "@/lib/startup-recovery";

// In the SSR runtime (Node.js) the Inertia server helper logs startup
// banners directly to stdout. That output interrupts the RSpec progress
// bar during system tests, so suppress only the known startup banners here.
// The browser runtime keeps console.log intact because `window` is defined
// there; console.error is left alone in SSR so real render errors remain
// visible.
if (typeof window === "undefined") {
  const SSR_STARTUP_MESSAGES = [
    "Inertia SSR server started.",
    "Starting SSR server on port",
    "Primary Inertia SSR server process started...",
  ];

  const originalLog = console.log;
  console.log = (...args: unknown[]) => {
    const message = args.map(String).join(" ");
    if (SSR_STARTUP_MESSAGES.some((banner) => message.includes(banner))) {
      return;
    }
    originalLog.apply(console, args);
  };
}

// Before `createInertiaApp`, so unhandled errors and promise rejections
// already arrive while the app boots. The function guards itself: without
// the `sentry-dsn` meta (dev/test) or without a browser (SSR) it is a no-op.
initMonitoring();

// Reports failed Inertia visits (network errors, non-Inertia responses) with
// operation context and prevents the unhandled rejection Inertia otherwise
// produces from a network error (issue #410). Guards itself without a
// browser (SSR).
initRouterErrorReporting();

// Registers the offline fallback of the installed app (Spec 021, R2).
// Guards itself without a browser (SSR) and without service worker support.
registerServiceWorker();

const nonce = readCspNonce();

if (nonce) {
  // Some libraries (e.g. Radix UI's scroll-lock helpers) inject `<style>`
  // tags and read their nonce from this webpack convention.
  window.__webpack_nonce__ = nonce;
}

// Resolves pages through our own seam instead of the plugin-generated
// resolver, which has no error path: a failing chunk import at boot flows
// into the catch below, and after boot (client-side navigation) it would
// otherwise die as an unhandled rejection with a broken page behind it
// (Sentry AMEALIO-FRONTEND-5/-6). Glob and extensions mirror the plugin's
// generated resolver exactly ("../pages" is relative to this file).
let booted = false;

const resolvePage = createPageResolver<ResolvedComponent>({
  loaders: import.meta.glob("../pages/**/*.{tsx,jsx}"),
  extensions: [".tsx", ".jsx"],
  onLoadFailure: (error) => {
    // Boot failures are recovered by the catch below; in SSR the failing
    // component render rejects the server render, which the SSR host
    // handles — reload and recovery loop guard are browser concerns.
    if (!booted || typeof window === "undefined") {
      return;
    }

    recoverFromStartupFailure(error, {
      storage: safeSessionStorage(),
      reload: () => window.location.reload(),
    });
  },
});

void createInertiaApp({
  nonce,

  resolve: resolvePage,

  strictMode: true,

  withApp: (app) => <ThemeProvider>{app}</ThemeProvider>,

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: "brackets" };
    },
  },
})
  .then(() => {
    // Successful boot clears the loop guard from a previous startup recovery,
    // so a failure on a later page load may reload again. From here on,
    // component load failures belong to navigations, not to this catch.
    booted = true;
    clearStartupReloadMark({ storage: safeSessionStorage() });
  })
  .catch((error) => {
    // This ensures this entrypoint is only loaded on Inertia pages
    // by checking for the presence of the root element (#app by default).
    // On Inertia pages a startup failure used to rethrow, which surfaced as
    // an unhandled rejection with an empty page behind it (issue #393). It
    // now reports to Sentry and reloads once, so the browser picks up fresh
    // assets after a deploy instead of leaving users on a blank page.
    if (document.getElementById("app")) {
      recoverFromStartupFailure(error, {
        storage: safeSessionStorage(),
        reload: () => window.location.reload(),
      });
    } else {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
      );
    }
  });
